<template>
  <div>
    <CI_SigningLinksModal
      :open="showSigningLinksModal"
      :document="signingLinksDocument"
      @close-modal="showSigningLinksModal = false"
    />
    <PreviewDocumentModal
      :open="isShowPreviewModal"
      @close-modal="isShowPreviewModal = false"
      :modal_config="{ record: previewDocumentRecord, file_url: previewDocumentFileURL }"
    />
    <SignInvestmentLoader
      v-if="UIStore.is_change_investment_loading"
      ref="player"
      :key="UIStore.is_dark_mode"
      :animation-data="animationData"
    />
    <div>
      <AsteriskNotice
        :text="documentsNoticeText"
        :variant="documentsNoticeVariant"
        size="large"
        class="hidden lg:flex"
      />
      <AsteriskNotice
        :text="documentsNoticeText"
        :variant="documentsNoticeVariant"
        size="medium"
        class="lg:hidden"
      />

      <!-- Stepper + Cards -->
      <div class="flex flex-col mt-6">
        <div v-for="(document, index) in documentsToSign" :key="index" class="flex gap-4">
          <!-- Stepper -->
          <div class="flex flex-col items-center w-6 shrink-0 relative">
            <div
              class="flex-1 w-[2px]"
              :class="index > 0 ? 'bg-strong opacity-30' : 'bg-transparent'"
            />
            <div
              class="w-6 h-6 rounded-full flex items-center justify-center shrink-0"
              :class="
                document.is_completed ? 'bg-success' : 'border-2 border-strong bg-transparent'
              "
            >
              <Icon
                v-if="document.is_completed"
                name="Check"
                class="h-4 w-4 text-always-light"
                :stroke-width="2.5"
              />
            </div>
            <div
              class="flex-1 w-[2px]"
              :class="
                index < documentsToSign.length - 1 ? 'bg-strong opacity-30' : 'bg-transparent'
              "
            />
          </div>

          <div class="flex-1 pt-2">
            <div
              class="bg-container-bg p-4 rounded-xl flex flex-col lg:flex-row items-start lg:items-center justify-between flex-1 min-h-[76px] gap-3 lg:gap-0"
            >
              <div
                class="flex flex-col lg:flex-row items-start lg:items-center gap-2 lg:gap-4 w-full lg:w-auto"
              >
                <!-- Status pill first on mobile, hidden on desktop -->
                <div
                  v-if="document.is_completed || document.is_pending"
                  :class="document.is_completed ? 'bg-green-600' : 'bg-info'"
                  class="rounded-full flex items-center gap-2 h-[18px] p-3 text-always-light lg:hidden mb-2"
                >
                  <Icon
                    v-if="document.is_completed"
                    name="Check"
                    class="h-4 w-4 text-white"
                    :stroke-width="2"
                  />
                  <Icon v-else name="Hourglass" class="h-4 w-4 text-white" :stroke-width="2" />
                  <p class="text-sm font-inter">
                    {{ document.is_completed ? "Signed" : "Pending" }}
                  </p>
                </div>

                <!-- Icon + Document name -->
                <div class="flex flex-col">
                  <div class="flex items-center gap-4">
                    <Icon name="FileText" class="text-cta-tertiary-outline-icon" />
                    <p class="font-inter font-medium text-strongest">
                      {{ document.name }}
                    </p>
                  </div>
                  <div
                    v-if="changeInvestmentStore.current_investment_for_change?.type === 'Joint'"
                    class="font-inter text-info text-sm flex items-center font-medium leading-[1.2rem] lg:hidden"
                  >
                    <Icon
                      name="Asterisk"
                      class="mr-1 h-[14px] w-[14px] -skew-x-12 stroke-info"
                      :stroke-width="3"
                    />
                    A joint signature is required for this document.
                  </div>
                </div>

                <!-- Status pill on desktop only -->
                <div>
                  <div
                    v-if="document.is_completed || document.is_pending"
                    :class="document.is_completed ? 'bg-green-600' : 'bg-info'"
                    class="rounded-full items-center gap-2 h-[18px] p-3 text-always-light hidden lg:flex"
                  >
                    <Icon
                      v-if="document.is_completed"
                      name="Check"
                      class="h-4 w-4 text-white"
                      :stroke-width="2"
                    />
                    <Icon v-else name="Hourglass" class="h-4 w-4 text-white" :stroke-width="2" />
                    <p class="text-sm font-inter">
                      {{ document.is_completed ? "Signed" : "Pending" }}
                    </p>
                  </div>
                </div>
                <div
                  v-if="changeInvestmentStore.current_investment_for_change?.type === 'Joint'"
                  class="font-inter text-info text-sm items-center font-medium leading-[1.2rem] hidden lg:flex"
                >
                  <Icon
                    name="Asterisk"
                    class="mr-1 h-[14px] w-[14px] -skew-x-12 stroke-info"
                    :stroke-width="3"
                  />
                  A joint signature is required for this document.
                </div>
              </div>

              <!-- CTAs -->
              <div class="w-full lg:w-auto flex items-center gap-2">
                <Button
                  v-if="document.is_completed"
                  variant="paper-primary"
                  @click="handlePreview(document)"
                >
                  <template #label>Preview</template>
                </Button>
                <!-- <Button
                  v-if="document.record && (document.is_completed || document.is_pending)"
                  variant="paper-primary"
                  @click="openSigningLinksModal(document)"
                >
                  <template #icon>
                    <Icon name="Link" class="h-4 w-4" />
                  </template>
                  <template #label>Signing Links</template>
                </Button> -->
                <Button
                  v-if="!document.is_completed && !document.pending_joint_signature"
                  variant="primary-grey"
                  @click="handleSign(document)"
                >
                  <template #label>Sign</template>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from "vue";
import { useDataStore, useChangeInvestmentStore, useUIStore } from "@/stores";
import { Button, Icon, AsteriskNotice, SignInvestmentLoader } from "@/components";
import preparingDocLight from "@/assets/animations/preparing-document-light.json";
import preparingDocDark from "@/assets/animations/preparing-document-dark.json";
import useFileUtility from "@/composables/useFileUtility";
import PreviewDocumentModal from "../InvestorDocuments/PreviewDocumentModal.vue";
import CI_SigningLinksModal from "./CI_SigningLinksModal.vue";
import { extractSigningLinkFromResponse } from "@/utilities";

const { fetchFileForPreview_R2 } = useFileUtility();

const dataStore = useDataStore();
const changeInvestmentStore = useChangeInvestmentStore();
const UIStore = useUIStore();

const isShowPreviewModal = ref(false);
const previewDocumentRecord = ref(null);
const previewDocumentFileURL = ref(null);
const showSigningLinksModal = ref(false);
const signingLinksDocument = ref(null);

const animationData = computed(() => (UIStore.is_dark_mode ? preparingDocDark : preparingDocLight));

onMounted(async () => {
  await changeInvestmentStore.handleInsertOrUpdateChildInvestments();
  changeInvestmentStore.startPollingDocuments();
});

onUnmounted(() => {
  changeInvestmentStore.stopPollingDocuments();
});

const investorName = computed(() => {
  return `${dataStore.contact.first_name} ${dataStore.contact.last_name}`;
});

const documentsToSign = computed(() => {
  const childDocs = changeInvestmentStore.change_investment_children.map((child, index) => {
    const doc = changeInvestmentStore.conversion_documents.find(
      (d) => d.investment_id === child.record_id
    );

    return {
      name: `S1 Agreement (${index + 1}) - ${investorName.value}`,
      is_completed: doc?.is_completed || false,
      is_pending: !!doc && !doc.is_completed,
      pending_joint_signature: doc?.pending_joint_signature || false,
      type: "s1_agreement",
      child_index: index,
      child_record_id: child.record_id,
      record: doc || null,
    };
  });

  const conversionAgreementDoc = changeInvestmentStore.conversion_agreement_doc;
  const conversionDoc = {
    name: "Conversion Agreement",
    is_completed: conversionAgreementDoc?.is_completed || false,
    is_pending: !!conversionAgreementDoc && !conversionAgreementDoc.is_completed,
    pending_joint_signature: conversionAgreementDoc?.pending_joint_signature || false,
    type: "conversion_agreement",
    child_index: null,
    child_record_id: null,
    record: conversionAgreementDoc || null,
  };

  return [...childDocs, conversionDoc];
});

const documentsNoticeVariant = computed(() => (documentsLeftToSign.value === 0 ? "info" : "error"));

const documentsNoticeText = computed(() => {
  if (documentsLeftToSign.value === 0) return "All documents signed";
  const count = documentsLeftToSign.value;
  return `You have ${count} document${count !== 1 ? "s" : ""} left to sign`;
});

const documentsLeftToSign = computed(() => {
  return documentsToSign.value.filter((d) => !d.is_completed).length;
});

function openSigningLinksModal(doc) {
  signingLinksDocument.value = doc;
  showSigningLinksModal.value = true;
}

async function handlePreview(doc) {
  if (!doc.record) return;

  const file_url = await fetchFileForPreview_R2(doc.record);
  previewDocumentRecord.value = doc.record;
  previewDocumentFileURL.value = file_url;
  isShowPreviewModal.value = true;
}

async function handleSign(document) {
  // If pending, grab the signing link from the existing record
  if (document.is_pending && document.record?.signing_links) {
    const signerLink = document.record.signing_links.find(
      (link) => link.role === "signer" && extractSigningLinkFromResponse(link)
    );
    const embedUrl = extractSigningLinkFromResponse(signerLink);
    if (embedUrl) {
      window.location.href = embedUrl;
      return;
    }
  }

  UIStore.is_change_investment_loading = true;

  let signingLink = null;

  if (document.type === "s1_agreement") {
    signingLink = await changeInvestmentStore.generateSigningLinkForChild(document.child_record_id);
  } else if (document.type === "conversion_agreement") {
    signingLink = await changeInvestmentStore.generateSigningLinkForConversionAgreement();
  }

  if (!signingLink) {
    UIStore.is_change_investment_loading = false;
    return;
  }

  await new Promise((resolve) => setTimeout(resolve, 15000));
  window.location.href = signingLink;
}

async function validate() {
  return { proceed: true };
}

defineExpose({ validate });
</script>
